import { ErrorLevel } from '@griply/utils';
import { jwtDecode } from 'jwt-decode';
import {
  QueryParamOptions,
  redirectWithParameters,
} from 'shared/lib/react-router-dom';
import { Paths } from 'shared/routes/paths';
import {
  getFeaturebaseToken,
  getGoals,
  getUser,
  signInWithDesktopCode,
  signInWithToken,
  signOutUser,
} from 'shared/services/backend-api';
import { getErrorReporter } from 'shared/services/error-tracking';
import { getFeaturebaseSsoRedirect } from 'shared/utils/get-featurebase-sso-redirect';
import { getSearchParamIdToken } from 'shared/utils/get-search-param-id-token';
import { isProductionEnvironment } from 'shared/utils/is-environment';

export const userLoader = async () => {
  if (!isProductionEnvironment) {
    // eslint-disable-next-line no-console -- non-prod logging
    console.log(
      `[Debug-Loader] userLoader started at ${performance.now().toFixed(0)}ms`,
    );
  }

  const user = await getUser();

  if (!user) {
    if (!isProductionEnvironment) {
      // eslint-disable-next-line no-console -- non-prod logging
      console.warn(
        '[Debug-Loader] ❌ getUser returned null. Triggering REDIRECT to Login.',
      );
    }
    throw redirectWithParameters(Paths.Login);
  }

  if (!isProductionEnvironment) {
    // eslint-disable-next-line no-console -- non-prod logging
    console.log('[Debug-Loader] ✅ User found, proceeding.');
  }
  return user;
};

export const loginLoader = async () => {
  // Sign-in strips the apple token from the url, and its state carries the redirect parameters.
  const appleIdToken = getSearchParamIdToken();
  await signInWithDesktopCode();
  await signInWithToken();
  const user = await getUser();

  if (user) {
    const desktopChallenge = new URLSearchParams(window.location.search).get(
      QueryParamOptions.DesktopChallenge,
    );

    // The browser half of the desktop hand-off ends here: mint the code on the confirmation page, not in a loader.
    if (desktopChallenge) {
      throw redirectWithParameters(Paths.DesktopConnected);
    }

    const appleStateParams = getAppleSignInStateParams(appleIdToken);
    // Google brings the sign-in params back on the url, apple only inside its state.
    const signInParams = new URLSearchParams({
      ...appleStateParams,
      ...Object.fromEntries(new URLSearchParams(window.location.search)),
    });
    const featurebaseReturnTo =
      signInParams.get(QueryParamOptions.Featurebase) === 'true'
        ? signInParams.get(QueryParamOptions.ReturnTo)
        : null;

    if (
      featurebaseReturnTo &&
      (await redirectToFeaturebaseSso(featurebaseReturnTo))
    ) {
      // A resolved loader would let React Router render /login while the browser is already leaving.
      return new Promise(() => {});
    }

    const goals = await getGoals();

    throw redirectWithParameters(
      goals.length ? Paths.Home : Paths.Goals,
      appleStateParams,
    );
  }

  return null;
};

const getAppleSignInStateParams = (
  appleIdToken: string | undefined,
): Record<string, string> => {
  if (!appleIdToken) {
    return {};
  }

  try {
    const { state } = jwtDecode<{ state?: string }>(appleIdToken);
    return state ? JSON.parse(state) : {};
  } catch (error) {
    getErrorReporter().captureException(error, {
      tags: { source: 'parse-search-params-jwt' },
    });
    return {};
  }
};

// False means the visitor continues into the app; both failures are reported.
const redirectToFeaturebaseSso = async (returnTo: string) => {
  // Resolved before the token is fetched, so a target outside the allow-list never sees one.
  const buildRedirectUrl = getFeaturebaseSsoRedirect(returnTo);

  if (!buildRedirectUrl) {
    getErrorReporter().captureMessage(
      'Invalid Featurebase SSO return_to URL',
      ErrorLevel.Warning,
      { tags: { source: 'featurebase-sso' }, extra: { returnTo } },
    );
    return false;
  }

  try {
    const { token } = await getFeaturebaseToken();
    // External redirect -- cannot use React Router redirect() for external URLs
    window.location.href = buildRedirectUrl(token);
    return true;
  } catch (error) {
    getErrorReporter().captureException(error, {
      tags: { source: 'featurebase-sso' },
    });
    return false;
  }
};

export const completeSignupLoader = async () => {
  // make sure that if someone has login credentials it is handled
  await signInWithToken();
  const user = await getUser();

  // make sure the user is signed out before continuing
  if (user) {
    await signOutUser();
  }

  return true;
};
